import { ContactLabel } from "@common-models/contact-label";
export * from './crud';
import { DBContactsLabelsThunks, contactsLabelsSelectByQuery, contactsLabelsSelectors, labelSelectors, contactsLabelsSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
import { ContentMessageServiceActions } from "@extension/context/content/services/contentMessageServiceActions";
import moment from 'moment';
import { toast } from 'sonner';
import { Snooze, labelActions } from "..";
import { hasTimeExpired } from "../helper-functions";
import { getArchivedContactIds, getContactLabelsByName, getInboxContactIds, getOpenIssueContactIds, getStarredContactIds, getUnreadContactIds, getSnoozedContactIds } from "../selectors";
import { selectUserObject } from "@common-reducers/UserSelectors";
import { accessFormPopupActions } from "@common-reducers/AccessFormPopupReducer";
import { createContactLabelThunk, loadLabelsAndContactLabelsThunk } from "./crud";
import { selectContactLabelIdsToRemove } from "../contact-label-dedupe";

const notify = (message: string) => toast.success(message);




// filter chats by label
export const filterChatsByLabelIdThunk = createAsyncThunk<void, string, { state: CommonRootState }>
    ("labelSlice/filterChatsThunk", async (labelId, thunkApi) => {
        console.log('[filterChatsByLabelIdThunk] START - labelId:', labelId);
        const dispatch = thunkApi.dispatch
        const state = thunkApi.getState()
        let label = labelSelectors.selectById(state, labelId)
        console.log('[filterChatsByLabelIdThunk] label from selector:', label);
        if (!label && state.DBLabelReducer?.entities) {
            label = state.DBLabelReducer.entities[labelId];
            console.log('[filterChatsByLabelIdThunk] label from entities fallback:', label);
        }

        let chatIds: string[] = []
        let isCustomLabelView = false

        if (!label || label.name.toLowerCase() === 'all') {
            console.log('[filterChatsByLabelIdThunk] Handling ALL - resetting filter');
            dispatch(labelActions.setActiveLabel(labelId))
            dispatch(labelActions.setChatListFilter(null))
            // Pass null to reset any active filters in WAPI
            ContentMessageServiceActions.waUpdateShouldAppearChatsByListTool(null)
            return
        } else if (label.name.toLowerCase() === 'needs attention') {
            chatIds = getOpenIssueContactIds(state)
            console.log('[filterChatsByLabelIdThunk] NEEDS ATTENTION - getOpenIssueContactIds returned:', chatIds.length, 'ids:', chatIds);
        } else if (label.name.toLowerCase() === 'snoozed') {
            chatIds = getSnoozedContactIds(state)
        } else if (label.name.toLowerCase() === 'archived') {
            dispatch(labelActions.setActiveLabel(labelId))
            ContentMessageServiceActions.waUpdateShouldAppearChatsByListTool({
                filterType: 'ARCHIVED',
                excludeIds: []
            });
            return;
        } else if (label.name.toLowerCase() === 'unread') {
            dispatch(labelActions.setActiveLabel(labelId))
            dispatch(labelActions.setChatListFilter({ type: 'UNREAD' }))
            ContentMessageServiceActions.waUpdateShouldAppearChatsByListTool({
                filterType: 'UNREAD',
                excludeIds: []
            });
            return;
        } else if (label.name.toLowerCase() === 'inbox zero') {
            const inboxContacts = getContactLabelsByName({ state, labelName: 'inbox' });
            chatIds = inboxContacts.map(cl => cl.waChatId);
            // Fall through to default logic which handles chatIds
        } else if (label.name.toLowerCase() === 'done') {
            // Done: Only chats with 'done' label
            const doneContacts = getContactLabelsByName({ state, labelName: 'done' });
            chatIds = doneContacts.map(cl => cl.waChatId);
        } else {
            isCustomLabelView = true
            console.log('[filterChatsByLabelIdThunk] Querying contactsLabels with labelId:', labelId);
            const contactsLabels = contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, {
                labelId,
            })
            console.log('[filterChatsByLabelIdThunk] contactsLabels found:', contactsLabels);
            chatIds = contactsLabels.map(cl => cl.waChatId).filter((id): id is string => id !== undefined)
            console.log('[filterChatsByLabelIdThunk] chatIds extracted:', chatIds);
        }

        const chatsThatShouldAppearInList = chatIds.reduce<Record<string, boolean>>((accumulator, chatId) => {
            accumulator[chatId] = true;
            return accumulator;
        }, {});

        console.log('[filterChatsByLabelIdThunk] chatsThatShouldAppearInList:', chatsThatShouldAppearInList);
        dispatch(labelActions.setActiveLabel(labelId))
        // Dashboard chat list (Team Inbox) applies the same filter via this
        // slice state — the WAPI call below only affects the WhatsApp Web UI.
        dispatch(labelActions.setChatListFilter({ type: 'IDS', chatIds }))

        // #770: the page-side filter reports the label's visible/archived/no-conversation breakdown,
        // computed from the same baseline and matcher as the filter itself (no second chat query to race).
        const snapshot = await ContentMessageServiceActions.waUpdateShouldAppearChatsByListTool(chatsThatShouldAppearInList)
        if (isCustomLabelView && snapshot && thunkApi.getState().LabelReducer.activeLabelId === labelId) {
            dispatch(labelActions.setLabelViewStats({ labelId, ...snapshot }))
        }
        console.log('[filterChatsByLabelIdThunk] END - called updateShouldAppearChatsByList');
    });

//Note to be updated with patchmany!!
export const clearLabelChatsThunk = createAsyncThunk<void, string, { state: CommonRootState }>
    ("labelSlice/clearLabelChatsThunk", async (labelId, thunkApi) => {
        const dispatch = thunkApi.dispatch
        const state = thunkApi.getState()
        const activeLabelId = state.LabelReducer.activeLabelId
        const label = labelSelectors.selectById(state, labelId)
        const contactLabels = contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, { labelId })
        contactLabels.forEach((contactLabel) => {
            dispatch(DBContactsLabelsThunks.delete({ entity: { _id: contactLabel._id } }))
        })
        window.postMessage({
            cmd: "clear-label-chats",
            payload: {
                labelName: label.name,
                isFilterEnabled: activeLabelId === labelId
            }
        }, "*");

        if (activeLabelId === labelId
        ) {
            dispatch(filterChatsByLabelIdThunk(activeLabelId))
        }
        notify(`Cleared all chats in ${label.name} label.`)
    });






// monitors snoozed contacts: unsnooze contact
export const removeContactFromSnoozedThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>
    ('labelSlice/removeContactFromSnoozedThunk', async (_, thunkAPI) => {
        const dispatch = thunkAPI.dispatch
        const state = thunkAPI.getState()
        const activeLabelId = state.LabelReducer.activeLabelId
        const snoozedContacts = getContactLabelsByName({ state, labelName: 'snoozed' })
        if (snoozedContacts.length) {
            let shouldRefreshFilter = false;
            // Use for...of instead of forEach to properly await async operations
            for (const contact of snoozedContacts) {
                const futureTime = moment(contact?.snooze?.expiryDate);
                // The future time that you are waiting to expire
                // Fix: isValid() must be called as a function, not accessed as a property
                if (futureTime.isValid() && hasTimeExpired(futureTime)) {
                    const res = await dispatch(DBContactsLabelsThunks.delete({ entity: { _id: contact._id } }))
                    if (res.payload) {
                        shouldRefreshFilter = true;
                    }
                }
            }
            // Only dispatch filter refresh once after all deletions
            if (shouldRefreshFilter) {
                dispatch(filterChatsByLabelIdThunk(activeLabelId))
            }
        }

    })

// monitors handle later contacts: when time expires, remove handle later and add inbox
export const processExpiredHandleLaterThunk = createAsyncThunk<void, undefined, { state: CommonRootState }>
    ('labelSlice/processExpiredHandleLaterThunk', async (_, thunkAPI) => {
        const dispatch = thunkAPI.dispatch
        const state = thunkAPI.getState()
        const activeLabelId = state.LabelReducer.activeLabelId
        const authUser = selectUserObject(state)

        if (!authUser?._id) return;

        // Get all "handle later" contacts
        const handleLaterContacts = getContactLabelsByName({ state, labelName: 'handle later' })

        if (handleLaterContacts.length === 0) return;

        // Get inbox label for adding back
        const inboxLabel = labelSelectors.selectAll(state).find(l => l.name.toLowerCase() === 'inbox')

        let shouldRefreshFilter = false;

        for (const contact of handleLaterContacts) {
            const expiryDate = contact?.snooze?.expiryDate || contact?.snooze_expired;
            if (!expiryDate) continue;

            const futureTime = moment(expiryDate);

            if (futureTime.isValid() && hasTimeExpired(futureTime)) {
                console.log('[handle-later] Expired contact found:', contact.waChatId, 'expiry:', expiryDate);

                // Remove the handle later label
                const deleteRes = await dispatch(DBContactsLabelsThunks.delete({ entity: { _id: contact._id } }))

                if (deleteRes.payload && inboxLabel) {
                    // Check if inbox label already exists for this contact
                    const existingInboxLabel = contactsLabelsSelectOneObjectByQuery(state.DBContactsLabelsReducer, {
                        labelId: inboxLabel._id,
                        waChatId: contact.waChatId
                    })

                    // Only add inbox if it doesn't already exist
                    if (!existingInboxLabel) {
                        const newInboxLabel: ContactLabel = {
                            waChatId: contact.waChatId,
                            labelId: inboxLabel._id,
                            labelName: 'inbox',
                            owner: authUser._id,
                        }
                        await dispatch(createContactLabelThunk({ contactLabel: newInboxLabel })).unwrap();
                        console.log('[handle-later] Added inbox label to:', contact.waChatId);
                    }

                    shouldRefreshFilter = true;
                }
            }
        }

        // Refresh filter if any contacts were processed
        if (shouldRefreshFilter) {
            dispatch(filterChatsByLabelIdThunk(activeLabelId))
            notify('Handle Later items returned to Inbox')
        }
    })



/* noam check */




// check if chat already has this label
export const isChatAlradyHaveThisLabel = (waChatId: string, labelId: string) => (dispatch: AppThunkDispatch, getState: () => CommonRootState) => {
    const state = getState();
    const contactLabel = contactsLabelsSelectOneObjectByQuery(state.DBContactsLabelsReducer, {
        labelId,
        waChatId
    })
    return contactLabel
}

// toggle chat label
export const toggleChatLabelThunk = createAsyncThunk<void, { waChatId: string, labelId: string, snooze?: Snooze }, { state: CommonRootState }>
    ("labelSlice/toggleChatLabelThunk", async ({ waChatId, labelId, snooze }, thunkApi) => {
        try {
            const dispatch = thunkApi.dispatch
            let state = thunkApi.getState()

            const authUser = selectUserObject(state)

            if (!authUser?._id) {
                dispatch(accessFormPopupActions.setStatus('Login'))
                return
            }

            // Labels are normally loaded on first WA-Web mount by
            // top-header-main's `loadLabelsAndContactLabelsThunk`
            // useEffect. But the toggle can race that — e.g. user
            // clicks a hover chip on a chat row before the header
            // useEffect runs (HMR, the labels feature mounting in a
            // surface where the top header isn't visible yet, cold
            // tab load where the chat list paints first). Instead of
            // silently dropping the click, trigger the load on
            // demand, await it, and refresh the local `state` so the
            // selectors below see the freshly-loaded labels.
            if (!state.DBLabelReducer?.itemsLoaded) {
                console.warn(
                    '[bt-labels] toggleChatLabelThunk - Labels not loaded yet, loading on demand',
                );
                await dispatch(loadLabelsAndContactLabelsThunk()).unwrap().catch((err) => {
                    console.error('[bt-labels] on-demand label load failed', err);
                });
                state = thunkApi.getState();
                if (!state.DBLabelReducer?.itemsLoaded) {
                    console.error(
                        '[bt-labels] toggleChatLabelThunk - Labels still not loaded after on-demand fetch',
                    );
                    return;
                }
            }

            if (!labelId) {
                console.error('[bt-labels] toggleChatLabelThunk - No labelId provided');
                return;
            }

            // Pass the full state, not just DBLabelReducer
            const label = labelSelectors.selectById(state, labelId)
            if (!label) {
                console.error('[bt-labels] toggleChatLabelThunk - Label not found for ID:', labelId);
                return;
            }

            // Membership is a SET, but the DB has accumulated duplicate rows for the
            // same (labelId, waChatId) — up to 12× on "inbox". Deleting only the first
            // one meant the chat stayed in the filtered list until you had clicked once
            // per duplicate. Remove the WHOLE set so one click always toggles off.
            const existingContactLabels = contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, {
                labelId,
                waChatId,
            })
            const idsToRemove = selectContactLabelIdsToRemove(existingContactLabels, { labelId, waChatId })

            if (idsToRemove.length) {
                if (idsToRemove.length > 1) {
                    console.warn(
                        `[bt-labels] toggleChatLabelThunk - removing ${idsToRemove.length} duplicate contact-label rows for`,
                        waChatId, label.name,
                    );
                }
                await Promise.all(idsToRemove.map((_id) =>
                    dispatch(DBContactsLabelsThunks.delete({ entity: { _id } }))
                ))
            } else {
                const contactLabel: ContactLabel = {
                    waChatId,
                    labelId,
                    labelName: label.name,
                    owner: authUser._id,
                    ...(snooze && {
                        snooze,
                        snooze_expired: snooze.expiryDate
                    })
                }
                await dispatch(createContactLabelThunk({ contactLabel })).unwrap();

                // If setting "handle later" with a snooze time, remove from inbox
                if (label.name.toLowerCase() === 'handle later' && snooze?.expiryDate) {
                    const inboxLabel = labelSelectors.selectAll(state).find(l => l.name.toLowerCase() === 'inbox')
                    if (inboxLabel) {
                        // Same duplicate-set removal as the toggle-off path above —
                        // leaving even one stale inbox row keeps the chat in Inbox.
                        const inboxIds = selectContactLabelIdsToRemove(
                            contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, {
                                labelId: inboxLabel._id,
                                waChatId,
                            }),
                            { labelId: inboxLabel._id, waChatId },
                        )
                        if (inboxIds.length) {
                            await Promise.all(inboxIds.map((_id) =>
                                dispatch(DBContactsLabelsThunks.delete({ entity: { _id } }))
                            ))
                            console.log('[handle-later] Removed inbox label from:', waChatId, `(${inboxIds.length} row(s))`);
                        }
                    }
                }
            }

            // if the active label is the one that was toggled, update the chats
            const activeLabelId = state.LabelReducer.activeLabelId
            const activeLabel = activeLabelId ? labelSelectors.selectById(state, activeLabelId) : null

            // Refresh if:
            // 1. The active label is the toggled label
            // 2. OR "handle later" was added while viewing inbox (contact should disappear from inbox)
            const isHandleLaterAddedWhileViewingInbox =
                label.name.toLowerCase() === 'handle later' &&
                snooze?.expiryDate &&
                activeLabel?.name?.toLowerCase() === 'inbox';

            if (activeLabelId === labelId || isHandleLaterAddedWhileViewingInbox) {
                dispatch(filterChatsByLabelIdThunk(activeLabelId))
            }

        } catch (error) {
            console.error('[bt-labels] toggleChatLabelThunk - ERROR:', error);
            throw error;
        }
    })














